// client/src/dnd/Sidebar_parts/character_parts/CharacterSheetPreview.js
import React, { useMemo, useState } from "react";

function safeNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function plus(n) {
  const x = safeNum(n, null);
  if (x == null) return "";
  return x >= 0 ? `+${x}` : `${x}`;
}

function abilityModFromScore(score) {
  const s = safeNum(score, null);
  if (s == null) return null;
  return Math.floor((s - 10) / 2);
}

function pick(obj, keys) {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  return undefined;
}

function sanitizeText(s) {
  if (s == null) return "";
  return String(s);
}

function stripHtml(html) {
  const s = sanitizeText(html);
  if (!s) return "";
  return s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function getFoundrySystem(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  return parsed.system || parsed.data || null;
}

function getAbility(system, key) {
  const ab = (system && system.abilities) || {};
  const node = ab[key] || null;
  const score = safeNum(pick(node, ["value", "score", "total"]), null);
  const mod = safeNum(pick(node, ["mod", "modifier"]), null);
  const computedMod = mod != null ? mod : abilityModFromScore(score);
  return { score, mod: computedMod };
}

function getProfBonus(system) {
  const attr = (system && system.attributes) || {};
  const pb = safeNum(pick(attr, ["prof"]), null);
  if (pb != null) return pb;
  return safeNum(system && system.prof, null);
}

function getHP(system) {
  const attr = (system && system.attributes) || {};
  const hp = pick(attr, ["hp"]) || pick(system || {}, ["hp"]) || {};
  return {
    value: safeNum(pick(hp, ["value", "current"]), null),
    max: safeNum(pick(hp, ["max"]), null),
    temp: safeNum(pick(hp, ["temp"]), null),
  };
}

function getAC(system) {
  const attr = (system && system.attributes) || {};
  const ac = pick(attr, ["ac"]) || pick(system || {}, ["ac"]) || {};
  return safeNum(pick(ac, ["value"]), null);
}

function getSpeed(system) {
  const attr = (system && system.attributes) || {};
  const mv = pick(attr, ["movement"]) || pick(system || {}, ["movement"]) || {};
  return {
    walk: safeNum(pick(mv, ["walk"]), null),
    fly: safeNum(pick(mv, ["fly"]), null),
    swim: safeNum(pick(mv, ["swim"]), null),
    climb: safeNum(pick(mv, ["climb"]), null),
    burrow: safeNum(pick(mv, ["burrow"]), null),
  };
}

function getInit(system) {
  const attr = (system && system.attributes) || {};
  const init = pick(attr, ["init"]) || pick(system || {}, ["initiative"]) || {};
  return safeNum(pick(init, ["mod", "value"]), null);
}

function getSenses(system) {
  const attr = (system && system.attributes) || {};
  const senses = pick(attr, ["senses"]) || pick(system || {}, ["senses"]) || {};
  return {
    darkvision: safeNum(pick(senses, ["darkvision"]), null),
    blindsight: safeNum(pick(senses, ["blindsight"]), null),
    tremorsense: safeNum(pick(senses, ["tremorsense"]), null),
    truesight: safeNum(pick(senses, ["truesight"]), null),
  };
}

function getDetails(system) {
  const details = (system && system.details) || {};
  const traits = (system && system.traits) || {};
  const level = safeNum(pick(details, ["level", "lvl"]), null);
  const race = pick(details, ["race"]) || pick(traits, ["race"]) || null;
  const background = pick(details, ["background"]) || null;
  const alignment = pick(details, ["alignment"]) || null;
  const xpNode = pick(details, ["xp"]) || {};
  const xp = safeNum(pick(xpNode, ["value", "current"]), null) ?? safeNum(pick(details, ["xp"]), null);
  const bioNode = pick(details, ["biography"]) || {};
  const bio = pick(bioNode, ["value", "public"]) || pick(details, ["biography"]) || "";
  return { level, race, background, alignment, xp, bio: sanitizeText(bio) };
}

function getClassesFromItems(items) {
  const out = [];
  if (!Array.isArray(items)) return out;
  for (const it of items) {
    if (!it || typeof it !== "object") continue;
    if (it.type === "class" || it.type === "subclass") {
      const sys = it.system || it.data || {};
      const levels = safeNum(pick(sys, ["levels", "level"]), null);
      out.push({ name: it.name || null, type: it.type, levels });
    }
  }
  return out;
}

function getPrimaryClassLabel(items, detailsLevel) {
  const classes = getClassesFromItems(items);
  const mainClass = classes.find((c) => c && c.type === "class") || null;
  const lvl = mainClass && mainClass.levels != null ? mainClass.levels : detailsLevel;
  if (!mainClass || !mainClass.name) return detailsLevel != null ? `Level ${detailsLevel}` : "";
  return lvl != null ? `${mainClass.name} ${lvl}` : `${mainClass.name}`;
}

function getSkills(system) {
  const skills = (system && system.skills) || {};
  const out = [];
  for (const key of Object.keys(skills)) {
    const s = skills[key];
    if (!s || typeof s !== "object") continue;
    const ability = pick(s, ["ability"]) || null;
    const profLevel = safeNum(pick(s, ["value", "proficient", "prof"]), null);
    const total = safeNum(pick(s, ["total", "mod"]), null);
    out.push({ key, ability, profLevel, total });
  }
  return out;
}

function skillDisplayName(key) {
  const map = {
    acr: "Acrobatics",
    ani: "Animal Handling",
    arc: "Arcana",
    ath: "Athletics",
    dec: "Deception",
    his: "History",
    ins: "Insight",
    itm: "Intimidation",
    inv: "Investigation",
    med: "Medicine",
    nat: "Nature",
    prc: "Perception",
    prf: "Performance",
    per: "Persuasion",
    rel: "Religion",
    slt: "Sleight of Hand",
    ste: "Stealth",
    sur: "Survival",
  };
  return map[key] || key;
}

function abilityShort(key) {
  const map = { str: "STR", dex: "DEX", con: "CON", int: "INT", wis: "WIS", cha: "CHA" };
  return map[key] || String(key || "").toUpperCase();
}

function buildCombatRows(parsedItems) {
  const items = Array.isArray(parsedItems) ? parsedItems : [];
  const out = [];
  for (const it of items) {
    if (!it || typeof it !== "object") continue;
    const type = it.type || "";
    if (type === "weapon" || type === "spell") {
      const sys = it.system || it.data || {};
      const range = pick(sys, ["range"]) || {};
      const r = safeNum(pick(range, ["value"]), null);
      const units = pick(range, ["units"]) || "";
      const rangeStr = r != null ? `${r} ${units || ""}`.trim() : "";
      const attackBonus = sanitizeText(pick(sys, ["attackBonus"])) || "";
      const actionType = sanitizeText(pick(sys, ["actionType"])) || "";
      const hit = attackBonus ? plus(attackBonus) : "";
      const dcNode = pick(sys, ["save"]) || {};
      const dc = safeNum(pick(dcNode, ["dc"]), null);
      const hitOrDc = dc != null ? `DC ${dc}` : hit ? `${hit} atk` : actionType ? actionType.toUpperCase() : "";
      const dmgNode = pick(sys, ["damage"]) || {};
      const parts = Array.isArray(dmgNode.parts) ? dmgNode.parts : [];
      let dmg = "";
      if (parts.length) {
        const p0 = parts[0];
        if (Array.isArray(p0) && p0.length) dmg = sanitizeText(p0[0]);
      }
      out.push({
        id: it._id || it.id || `${type}-${it.name}`,
        name: it.name || "",
        type,
        range: rangeStr,
        hitOrDc,
        damage: dmg,
      });
    }
  }
  return out.slice(0, 50);
}

function buildListByTypes(parsedItems, types) {
  const items = Array.isArray(parsedItems) ? parsedItems : [];
  const set = new Set(types || []);
  const out = [];
  for (const it of items) {
    if (!it || typeof it !== "object") continue;
    if (!set.has(it.type)) continue;
    const sys = it.system || it.data || {};
    const qty = safeNum(pick(sys, ["quantity", "qty"]), null);
    out.push({
      id: it._id || it.id || `${it.type}-${it.name}`,
      name: it.name || "",
      type: it.type || "",
      qty,
    });
  }
  return out;
}

function pretty(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

export default function CharacterSheetPreview(props) {
  const { preview } = props;

  const [tab, setTab] = useState("combat");

  const derived = useMemo(() => {
    const parsed = preview && preview.parsed ? preview.parsed : null;
    const system = getFoundrySystem(parsed) || null;
    const items = (parsed && parsed.items) || [];
    const name = (preview && preview.normalized && preview.normalized.name) || (parsed && parsed.name) || "Unnamed Character";

    const details = system ? getDetails(system) : { level: null, race: null, background: null, alignment: null, xp: null, bio: "" };
    const classLabel = getPrimaryClassLabel(items, details.level);
    const prof = system ? getProfBonus(system) : null;
    const init = system ? getInit(system) : null;
    const hp = system ? getHP(system) : { value: null, max: null, temp: null };
    const ac = system ? getAC(system) : null;
    const speed = system ? getSpeed(system) : { walk: null, fly: null, swim: null, climb: null, burrow: null };
    const senses = system ? getSenses(system) : { darkvision: null, blindsight: null, tremorsense: null, truesight: null };

    const abilities = system
      ? {
          str: getAbility(system, "str"),
          dex: getAbility(system, "dex"),
          con: getAbility(system, "con"),
          int: getAbility(system, "int"),
          wis: getAbility(system, "wis"),
          cha: getAbility(system, "cha"),
        }
      : {
          str: { score: null, mod: null },
          dex: { score: null, mod: null },
          con: { score: null, mod: null },
          int: { score: null, mod: null },
          wis: { score: null, mod: null },
          cha: { score: null, mod: null },
        };

    const skillsRaw = system ? getSkills(system) : [];
    const skills = skillsRaw
      .map((s) => {
        if (s.total != null) return { ...s, display: s.total };
        const abMod = s.ability && abilities[s.ability] ? abilities[s.ability].mod : null;
        const pb = prof != null ? prof : null;
        if (abMod == null || pb == null || s.profLevel == null) return { ...s, display: null };
        const p = s.profLevel === 0.5 ? 0.5 : s.profLevel === 2 ? 2 : s.profLevel === 1 ? 1 : 0;
        return { ...s, display: Math.floor(abMod + pb * p) };
      })
      .sort((a, b) => skillDisplayName(a.key).localeCompare(skillDisplayName(b.key)));

    const combatRows = buildCombatRows(items);
    const spells = buildListByTypes(items, ["spell"]);
    const inventory = buildListByTypes(items, ["weapon", "equipment", "tool", "consumable", "loot", "backpack"]);
    const features = buildListByTypes(items, ["feat", "class", "subclass", "race", "background"]);

    return {
      name,
      details,
      classLabel,
      prof,
      init,
      hp,
      ac,
      speed,
      senses,
      abilities,
      skills,
      combatRows,
      spells,
      inventory,
      features,
      rawJson: parsed,
      rawText: preview && preview.raw && preview.raw.text ? preview.raw.text : "",
      isBinary: preview && preview.raw && preview.raw.type === "binary",
      objectUrl: preview && preview.objectUrl ? preview.objectUrl : null,
      fileMeta: preview && preview.file ? preview.file : null,
    };
  }, [preview]);

  const styles = useMemo(() => {
    const panel = {
      background: "#121316",
      border: "1px solid #2a2d33",
      borderRadius: 14,
      boxShadow: "0 16px 60px #000c",
    };

    const pill = (active) => ({
      appearance: "none",
      border: "1px solid " + (active ? "#5b2b2b" : "#2a2d33"),
      background: active ? "#2a0f12" : "#0d0e10",
      color: active ? "#ffd6d9" : "#d7d9de",
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 900,
      cursor: "pointer",
      fontSize: 12,
    });

    const boxTitle = { fontSize: 11, fontWeight: 900, opacity: 0.85, letterSpacing: 0.4 };
    const boxValue = { fontSize: 16, fontWeight: 950 };
    const boxSub = { fontSize: 11, opacity: 0.75, marginTop: 2 };

    return { panel, pill, boxTitle, boxValue, boxSub };
  }, []);

  const abilityKeys = ["str", "dex", "con", "int", "wis", "cha"];

  return (
    <div
      style={{
        background: "#0a0b0d",
        border: "1px solid #222",
        borderRadius: 14,
        padding: 12,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
        <div style={{ ...styles.panel, padding: 12, flex: "1 1 320px", minWidth: 300 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: 12,
                background: "#1a1b20",
                border: "1px solid #2a2d33",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 950,
                fontSize: 18,
              }}
            >
              {derived.details.level != null ? String(derived.details.level) : "?"}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 950, fontSize: 18, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {derived.name}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                {derived.details.race || ""}{derived.details.race && derived.classLabel ? " · " : ""}{derived.classLabel || ""}
              </div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                {derived.details.background || ""}{derived.details.background && derived.details.alignment ? " · " : ""}{derived.details.alignment || ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 900 }}>PROF</div>
                <div style={{ fontSize: 16, fontWeight: 950 }}>{derived.prof != null ? plus(derived.prof) : "—"}</div>
              </div>
              <div style={{ width: 1, height: 34, background: "#2a2d33" }} />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 900 }}>INIT</div>
                <div style={{ fontSize: 16, fontWeight: 950 }}>{derived.init != null ? plus(derived.init) : "—"}</div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ ...styles.panel, padding: 10, flex: "1 1 180px", minWidth: 180, background: "#0d0e10" }}>
              <div style={styles.boxTitle}>HIT POINTS</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                <div style={styles.boxValue}>{derived.hp.value != null ? derived.hp.value : "—"}</div>
                <div style={{ opacity: 0.7, fontWeight: 900 }}>/</div>
                <div style={{ fontSize: 14, fontWeight: 900, opacity: 0.9 }}>{derived.hp.max != null ? derived.hp.max : "—"}</div>
                <div style={{ marginLeft: "auto", fontSize: 11, opacity: 0.75 }}>
                  TEMP {derived.hp.temp != null ? derived.hp.temp : 0}
                </div>
              </div>
              <div style={styles.boxSub}>Current / Max</div>
            </div>

            <div style={{ ...styles.panel, padding: 10, width: 120, background: "#0d0e10" }}>
              <div style={styles.boxTitle}>AC</div>
              <div style={{ ...styles.boxValue, marginTop: 6 }}>{derived.ac != null ? derived.ac : "—"}</div>
              <div style={styles.boxSub}>Armor Class</div>
            </div>

            <div style={{ ...styles.panel, padding: 10, width: 140, background: "#0d0e10" }}>
              <div style={styles.boxTitle}>SPEED</div>
              <div style={{ ...styles.boxValue, marginTop: 6 }}>{derived.speed.walk != null ? derived.speed.walk : "—"}</div>
              <div style={styles.boxSub}>ft (walk)</div>
            </div>
          </div>

          <div style={{ marginTop: 12, ...styles.panel, padding: 10, background: "#0d0e10" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {abilityKeys.map((k) => {
                const a = derived.abilities[k] || {};
                return (
                  <div
                    key={k}
                    style={{
                      flex: "1 1 90px",
                      minWidth: 90,
                      border: "1px solid #2a2d33",
                      borderRadius: 12,
                      padding: 10,
                      background: "#0a0b0d",
                    }}
                  >
                    <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 950 }}>{abilityShort(k)}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                      <div style={{ fontSize: 18, fontWeight: 950 }}>{a.mod != null ? plus(a.mod) : "—"}</div>
                      <div style={{ fontSize: 11, opacity: 0.75 }}>{a.score != null ? a.score : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ ...styles.panel, padding: 12, flex: "0 0 280px", minWidth: 260 }}>
          <div style={{ fontWeight: 950, fontSize: 12, opacity: 0.9, marginBottom: 8 }}>SKILLS</div>
          <div style={{ borderTop: "1px solid #2a2d33" }} />
          <div style={{ marginTop: 8 }}>
            {derived.skills.length ? (
              derived.skills.map((s) => (
                <div
                  key={s.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 6px",
                    borderRadius: 10,
                  }}
                >
                  <div style={{ width: 42, fontSize: 11, opacity: 0.75, fontWeight: 900 }}>
                    {abilityShort(s.ability)}
                  </div>
                  <div style={{ flex: 1, fontSize: 12, fontWeight: 800, opacity: 0.95 }}>
                    {skillDisplayName(s.key)}
                  </div>
                  <div style={{ width: 44, textAlign: "right", fontWeight: 950 }}>
                    {s.display != null ? plus(s.display) : "—"}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, opacity: 0.75 }}>No skills detected.</div>
            )}
          </div>

          <div style={{ marginTop: 12, borderTop: "1px solid #2a2d33", paddingTop: 10 }}>
            <div style={{ fontWeight: 950, fontSize: 12, opacity: 0.9, marginBottom: 8 }}>SENSES</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Darkvision: {derived.senses.darkvision != null ? `${derived.senses.darkvision} ft` : "—"}
            </div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Blindsight: {derived.senses.blindsight != null ? `${derived.senses.blindsight} ft` : "—"}
            </div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Tremorsense: {derived.senses.tremorsense != null ? `${derived.senses.tremorsense} ft` : "—"}
            </div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Truesight: {derived.senses.truesight != null ? `${derived.senses.truesight} ft` : "—"}
            </div>
          </div>
        </div>

        <div style={{ ...styles.panel, padding: 12, flex: "1 1 420px", minWidth: 320 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.pill(tab === "combat")} onClick={() => setTab("combat")}>COMBAT</button>
            <button style={styles.pill(tab === "spells")} onClick={() => setTab("spells")}>SPELLS</button>
            <button style={styles.pill(tab === "inventory")} onClick={() => setTab("inventory")}>INVENTORY</button>
            <button style={styles.pill(tab === "features")} onClick={() => setTab("features")}>FEATURES & TRAITS</button>
            <button style={styles.pill(tab === "notes")} onClick={() => setTab("notes")}>NOTES</button>
            <button style={styles.pill(tab === "about")} onClick={() => setTab("about")}>ABOUT</button>
            <button style={styles.pill(tab === "raw")} onClick={() => setTab("raw")}>RAW</button>
          </div>

          <div style={{ marginTop: 12 }}>
            {tab === "combat" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>ATTACKS</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                <div style={{ marginTop: 8 }}>
                  {derived.combatRows.length ? (
                    derived.combatRows.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.4fr 0.8fr 0.8fr 1fr",
                          gap: 10,
                          padding: "8px 6px",
                          borderBottom: "1px solid #1f2227",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontWeight: 900, fontSize: 12, opacity: 0.95 }}>{r.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>{r.range || "—"}</div>
                        <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900 }}>{r.hitOrDc || "—"}</div>
                        <div style={{ fontSize: 12, opacity: 0.85 }}>{r.damage || "—"}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.75 }}>No attacks/spells detected.</div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === "spells" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>SPELLS</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                <div style={{ marginTop: 8 }}>
                  {derived.spells.length ? (
                    derived.spells.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 6px",
                          borderBottom: "1px solid #1f2227",
                        }}
                      >
                        <div style={{ flex: 1, fontWeight: 900, fontSize: 12 }}>{r.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>{r.type}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.75 }}>No spells detected.</div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === "inventory" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>INVENTORY</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                <div style={{ marginTop: 8 }}>
                  {derived.inventory.length ? (
                    derived.inventory.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 90px 60px",
                          gap: 10,
                          padding: "8px 6px",
                          borderBottom: "1px solid #1f2227",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontWeight: 900, fontSize: 12 }}>{r.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>{r.type}</div>
                        <div style={{ fontSize: 12, textAlign: "right", opacity: 0.9 }}>
                          {r.qty != null ? `x${r.qty}` : ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.75 }}>No inventory items detected.</div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === "features" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>FEATURES & TRAITS</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                <div style={{ marginTop: 8 }}>
                  {derived.features.length ? (
                    derived.features.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 120px",
                          gap: 10,
                          padding: "8px 6px",
                          borderBottom: "1px solid #1f2227",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontWeight: 900, fontSize: 12 }}>{r.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.75, textAlign: "right" }}>{r.type}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.75 }}>No features detected.</div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === "notes" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>NOTES</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85, lineHeight: 1.5 }}>
                  {derived.details.bio ? stripHtml(derived.details.bio) : "No notes/biography detected."}
                </div>
              </div>
            ) : null}

            {tab === "about" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>ABOUT</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.9, lineHeight: 1.6 }}>
                  <div>Alignment: {derived.details.alignment || "—"}</div>
                  <div>Background: {derived.details.background || "—"}</div>
                  <div>XP: {derived.details.xp != null ? derived.details.xp : "—"}</div>
                  <div>Speed: {derived.speed.walk != null ? `${derived.speed.walk} ft` : "—"}</div>
                  <div>File: {derived.fileMeta ? `${derived.fileMeta.name} (${derived.fileMeta.size_human || ""})` : "—"}</div>
                  {derived.objectUrl ? <div>Local open: available</div> : null}
                </div>
              </div>
            ) : null}

            {tab === "raw" ? (
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900, marginBottom: 8 }}>RAW</div>
                <div style={{ borderTop: "1px solid #2a2d33" }} />
                {derived.isBinary ? (
                  <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
                    Binary file. Use “Open file locally” in the upload modal.
                  </div>
                ) : (
                  <pre
                    style={{
                      marginTop: 10,
                      padding: 10,
                      borderRadius: 12,
                      background: "#0a0b0d",
                      border: "1px solid #222",
                      overflow: "auto",
                      maxHeight: 520,
                      fontSize: 12,
                      whiteSpace: "pre",
                    }}
                  >
                    {pretty(derived.rawJson)}
                  </pre>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
